import { LitElement, css, html } from 'lit';
import { when } from 'lit-html/directives/when.js';
import { createRef, ref } from 'lit/directives/ref.js';
import './slider-slide';

// Maximally handle scroll events every X ms
const DEBOUNCE_TIME = 30;
// Time to wait after a scroll event to assume it settled
const SCROLL_END_TIMEOUT = 100;
// Time to wait before updating aria attributes
// (to prevent speaking out all slides while quickly sliding through multiple slides)
const UPDATE_ARIA_TIMEOUT = 200;
// Minimum pixels to scroll before starting to handle the scroll
const SCROLL_THRESHOLD = 25;

/**
 * A slider using the scroll snap feature.
 *
 * The slider should get the attribute aria-live="polite".
 */
export class FlensSlider extends LitElement {
	static properties = {
		noNavigation: { type: Boolean, attribute: 'no-navigation' },
		noAria: { type: Boolean, attribute: 'no-aria' },
	};

	static styles = css`
    :host {
      box-sizing: border-box;
      display: inline-flex;
      position: relative;
    }

    .container {
      display: flex;
      gap: var(--slider-gap, 1rem);
      max-block-size: max-content;
      min-block-size: min-content;
      overflow: auto hidden;
      overscroll-behavior-x: contain;
      scroll-behavior: auto;
      scroll-snap-align: center;
      scroll-snap-stop: always;
      scroll-snap-type: x mandatory;
      scrollbar-width: none; /* Firefox */

      &::-webkit-scrollbar {
        display: none;
      }

      &.free {
        scroll-snap-type: none;
      }
    }

    ::slotted(*) {
      display: flex;
      min-inline-size: var(--slide-width, 100%);
    }

    @media (prefers-reduced-motion: no-preference) {
      .container {
        scroll-behavior: smooth;

        &.immediate {
          scroll-behavior: auto;
        }
      }

      ::slotted(*) {
        transition: 0.3s all ease-out;
      }
    }
  `;

	constructor() {
		super();
		this._onScroll = this._onScroll.bind(this);
		this._onScrollEnd = this._onScrollEnd.bind(this);
		this._onTouchStart = this._onTouchStart.bind(this);
		this._onTouchEnd = this._onTouchEnd.bind(this);
		this._onPossibleScrollEnd = this._onPossibleScrollEnd.bind(this);
		this._handleScroll = this._handleScroll.bind(this);
		this._onResize = this._onResize.bind(this);

		this.noNavigation = false;
		this.noAria = false;

		this._containerRef = createRef();
		this._isScrolling = false;
		this._isMouseDown = false;
		this._slideWidth = undefined;
		this._activeIndex = 0;
		this._lastScrollPos = 0;
		this._scrollEndTimer = 0;
		this._handleScrollTimer = 0;
		this._ariaTimer = 0;
		this._ariaActiveIndex = 0;
	}

	/** @returns {number} */
	get count() {
		return this.slides.length;
	}

	/** @returns {number} */
	get scrollPos() {
		return this._containerRef.value.scrollLeft;
	}

	/** @returns {boolean} */
	get canGoPrevious() {
		return this._activeIndex > 0;
	}

	/** @returns {boolean} */
	get canGoNext() {
		return this._activeIndex < this.count - 1;
	}

	/** @returns {HTMLElement[]} */
	get slides() {
		return this._containerRef.value.children[0]
			.assignedElements()
			.flatMap((element) => element.assignedElements?.() ?? element)
			.filter((slide) => !slide.clone);
	}

	/** @returns {HTMLElement} */
	get activeSlide() {
		return this.slides[this.activeIndex];
	}

	/** @returns {number} */
	get activeIndex() {
		return this._activeIndex;
	}

	/** @returns {boolean} */
	get isScrolling() {
		return this._isScrolling;
	}

	/**
	 * Get the slide width.
	 * @returns {number} The width of a slide in pixels including slide gap.
	 */
	get slideWidth() {
		return this._slideWidth ?? this._getSlideWidth();
	}

	/**
	 * Move to the given slide index.
	 * @param {number} index The index of the slide to move to.
	 * @param {boolean} [immediate] Whether to move to the target position
	 *   immediately or smoothly scroll.
	 */
	moveToIndex(index, immediate = false) {
		const { slides } = this;
		if (index < 0 || index >= slides.length) {
			console.error('Index out of range', index);
			return;
		}
		this.moveToSlide(slides[index], immediate);
	}

	/**
	 * Move to the given slide.
	 * @param {HTMLElement} slide The slide to move to.
	 * @param {boolean} [immediate] Whether to move to the target position
	 *   immediately or smoothly scroll.
	 */
	moveToSlide(slide, immediate = false) {
		if (!slide) {
			console.error('Missing slide parameter', slide);
			return;
		}
		const container = this._containerRef.value;
		container.classList.toggle('immediate', immediate);
		container.scrollLeft =
			slide.offsetLeft +
			slide.offsetWidth -
			container.offsetLeft -
			container.offsetWidth;
	}

	/**
	 * Move in the given direction.
	 * @param {number} direction The number of slides to move, can be negative to
	 *   move to previous slides.
	 * @param {boolean} [immediate] Whether to move to the target position
	 *   immediately or smoothly scroll.
	 */
	move(direction, immediate = false) {
		if (direction === 0) {
			return;
		}
		const container = this._containerRef.value;
		container.classList.toggle('immediate', immediate);
		container.scrollLeft = container.scrollLeft + this.slideWidth * direction;
	}

	/**
	 * @param {number} fraction
	 * @param {boolean} [immediate] Whether to move to the target position
	 *   immediately or smoothly scroll.
	 */
	moveToFraction(fraction, immediate = false) {
		const container = this._containerRef.value;
		container.classList.toggle('immediate', immediate);
		container.classList.toggle('free', immediate);
		container.scrollLeft = this.slideWidth * fraction;
	}

	/**
	 * Called on “scroll” events.
	 *
	 * Calls {@link _handleScroll} debounced and {@link _onPossibleScrollEndDebounced}
	 * when there are no “scroll” events for some time to detect the end of the scroll
	 * including finished smooth scroll snap.
	 * @private
	 */
	_onScroll() {
		if (this._handleScrollTimer) {
			return; // Debounce
		}
		const { scrollPos } = this;
		if (
			!this.isScrolling &&
			Math.abs(this._lastScrollPos - scrollPos) < SCROLL_THRESHOLD
		) {
			return;
		}
		this._lastScrollPos = scrollPos;
		if (!this._isScrolling) {
			this._onScrollStart();
		}
		this._handleScrollTimer = setTimeout(this._handleScroll, DEBOUNCE_TIME);
		this._onPossibleScrollEndDebounced();
	}

	/**
	 * Scrolling starts.
	 * @private
	 */
	_onScrollStart() {
		// Cache current slide width
		this._isScrolling = true;
		this._slideWidth = this._getSlideWidth();
	}

	/**
	 * Scrolling has most probably finished.
	 * Unfortunately we cannot yet use the “scrollend” event,
	 * because Apple does not yet support it.
	 * @private
	 */
	_onScrollEnd() {
		this._isScrolling = false;
		this._containerRef.value.classList.remove('immediate', 'free');
		this._slideWidth = undefined;
		clearTimeout(this._handleScrollTimer);
		clearTimeout(this._scrollEndTimer);
		this._handleScroll(true);

		// Fix possible errors in active index, can happen after resizes
		this._updateIndex();

		this.dispatchEvent(new Event('changed'));
	}

	/**
	 * Scrolling may have finished, no “scroll” events for some time.
	 * Only needed because Apple doesn't support the “scrollend” event.
	 * @private
	 */
	_onPossibleScrollEnd() {
		if (this._isMouseDown) {
			this._onPossibleScrollEndDebounced();
			return;
		}
		this._onScrollEnd();
	}

	/**
	 * Call {@link _onPossibleScrollEnd} debounced.
	 * @private
	 */
	_onPossibleScrollEndDebounced() {
		clearTimeout(this._scrollEndTimer);
		this._scrollEndTimer = setTimeout(
			this._onPossibleScrollEnd,
			SCROLL_END_TIMEOUT,
		);
	}

	/**
	 * Touch start or mouse down.
	 * @private
	 */
	_onTouchStart() {
		this._isMouseDown = true;
	}

	/**
	 * Touch ent or mouse up.
	 * @private
	 */
	_onTouchEnd() {
		this._isMouseDown = false;
		if (this.isScrolling) {
			this._onPossibleScrollEndDebounced();
		}
	}

	/**
	 * Debounced call from “scroll” event.
	 * @returns {{fraction: number}}
	 * @private
	 */
	_handleScroll(isEnd = false) {
		this._handleScrollTimer = 0;
		if (!this._slideWidth) {
			this._updateSlides(true);
			return { fraction: 0 }; // can happen when just tapping inside the container without swiping
		}

		const fraction = this._containerRef.value.scrollLeft / this._slideWidth;
		if (!isEnd) {
			// eslint-disable-next-line compat/compat
			this.dispatchEvent(new CustomEvent('changing', { detail: fraction }));
		}

		const { slides } = this;
		const maxIndex = slides.length - 1;
		const activeIndex = Math.max(0, Math.min(maxIndex, Math.round(fraction)));
		if (this._activeIndex !== activeIndex) {
			slides[this._activeIndex].active = false;
			slides[activeIndex].active = true;
			this._activeIndex = activeIndex;
			this._updateSlides(isEnd);
			this.dispatchEvent(new Event('change'));
		}
		return { fraction };
	}

	/**
	 * Get the width of one slide. This is cached in {@link _slideWidth} while scrolling.
	 * @returns {number}
	 * @private
	 */
	_getSlideWidth() {
		const firstSlide = this.slides[0];
		if (!firstSlide) {
			return 0;
		}
		const container = this._containerRef.value;
		return (
			Number.parseInt(getComputedStyle(container).columnGap) +
			firstSlide.offsetWidth
		);
	}

	/**
	 * Update the active property of slides, update aria attributes
	 * and updates focus on scroll end.
	 * @param {boolean} [isEnd] Whether called on scroll end
	 * @private
	 */
	_updateSlides(isEnd = false) {
		if (this.noAria) {
			return;
		}

		clearTimeout(this._ariaTimer);
		this._ariaTimer = setTimeout(() => {
			if (this._activeIndex === this._ariaActiveIndex) {
				return; // No change
			}
			const { slides } = this;
			const newSlide = slides[this._activeIndex];
			this._updateSlideAria(slides[this._ariaActiveIndex], isEnd);
			this._updateSlideAria(newSlide, isEnd);
			this._ariaActiveIndex = this._activeIndex;
			if (this.contains(document.activeElement)) {
				newSlide.focus();
			}
		}, UPDATE_ARIA_TIMEOUT);
	}

	_updateSlideAria(slide) {
		// This triggers announcements via aria-live
		slide.setAttribute('aria-hidden', slide.active ? 'false' : 'true');
		slide.setAttribute('aria-selected', slide.active ? 'true' : 'false');
		slide.setAttribute('tabindex', slide.active ? '0' : '-1');

		const focusables = slide.querySelectorAll('button,a');
		if (slide.active) {
			for (const elem of focusables) {
				elem.removeAttribute('tabindex');
			}
		} else {
			for (const elem of focusables) {
				elem.setAttribute('tabindex', '-1');
			}
		}
	}

	_updateIndex() {
		const fraction = this._containerRef.value.scrollLeft / this.slideWidth;
		const { slides } = this;
		const maxIndex = slides.length - 1;
		const activeIndex = Math.max(0, Math.min(maxIndex, Math.round(fraction)));
		if (this._activeIndex !== activeIndex) {
			slides[this._activeIndex].active = false;
			slides[activeIndex].active = true;
			this._activeIndex = activeIndex;
		}
		if (this._ariaActiveIndex !== activeIndex) {
			this._updateSlides(true);
		}
	}

	_shouldHandleResize() {
		return true;
	}

	_onResize() {
		if (this._ignoreInitialResize) {
			this._ignoreInitialResize = undefined;
			return;
		}
		if (this._isScrolling) {
			this._slideWidth = this._getSlideWidth();
		}
		if (this._resizeTimer || !this._shouldHandleResize()) {
			return;
		}
		const restoreIndex = this.activeIndex;
		clearTimeout(this._resizeTimer);
		this._resizeTimer = setTimeout(() => {
			this.moveToIndex(restoreIndex);
			this._resizeTimer = undefined;
		}, 300);
	}

	connectedCallback() {
		this._ignoreInitialResize = true;
		this._resizeObserver = new ResizeObserver(this._onResize);
		this._resizeObserver.observe(this);
		super.connectedCallback();
	}

	disconnectedCallback() {
		this._resizeObserver.disconnect();
		this._resizeObserver = null;
		super.disconnectedCallback();
	}

	firstUpdated(_changedProperties) {
		const container = this._containerRef.value;
		container.addEventListener('scroll', this._onScroll);
		container.addEventListener('touchstart', this._onTouchStart);
		container.addEventListener('mousedown', this._onTouchStart);
		container.addEventListener('mouseup', this._onTouchEnd);
		container.addEventListener('touchend', this._onTouchEnd);
		super.firstUpdated(_changedProperties);
	}

	render() {
		return html`
      ${when(
				!this.noNavigation,
				() => html`
          <slot name="navigation">
            <flens-slider-navigation></flens-slider-navigation>
          </slot>
        `,
			)}
      <div ${ref(this._containerRef)} class="container" part="container">
        <slot></slot>
      </div>
    `;
	}
}

customElements.define('flens-slider', FlensSlider);
